import { domMax, m } from 'framer-motion';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { CapiPlp, GridSize } from 'types/capi/plp';
import type {
  ProductListItem,
  ProductListItemExternal,
  ProductListItemInternal
} from 'types/capi/shared';

import FramerMotion from '~/components/FramerMotion';
import ProductCard from '~/components/ProductCard';
import usePageData from '~/hooks/usePageData';
import useQsParams from '~/hooks/useQsParams';
import useRegion from '~/hooks/useRegion';

import DownloadAppBanner from '../Banners/DownloadAppBanner';
import DirectProductsCarousel from '../DirectProductsCarousel';
import { MidModuleSection } from '../ModuleSection';
import Pagination from './Pagination';

const gridStyles = {
  large: 'grid-cols-1 md:grid-cols-3 ',
  small: 'grid-cols-2 md:grid-cols-4'
};

const Products = ({
  gridSize = 'small',
  allowNotifications
}: {
  allowNotifications: boolean;
  gridSize: GridSize;
}) => {
  const { q } = useQsParams();
  const plp = usePageData<CapiPlp>();
  const region = useRegion();

  return (
    <div className={!q ? 'min-h-screen py-6' : 'py-6'}>
      {plp.products.length > 0 ? (
        <div className="flex flex-col gap-4">
          <div className={`grid gap-2 md:gap-6 ${gridStyles[gridSize]}`}>
            {plp.products.map((product, index) => (
              <React.Fragment key={`plp-product-${index}`}>
                {index === 8 && region.code === 'GB' && (
                  <DirectProductsCarousel />
                )}
                {index === 12 && <MidModuleSection />}
                <FramerMotion key={product.id} feature={domMax}>
                  <m.div layout>
                    {product.type === 'external' ? (
                      <ProductCardExternal
                        key={`product-${product.id}-${index}`}
                        allowNotifications={allowNotifications}
                        product={product as ProductListItemExternal}
                        index={index}
                      />
                    ) : (
                      <ProductCardInternal
                        product={product as ProductListItemInternal}
                        allowNotifications={allowNotifications}
                        index={index}
                        gridSize={gridSize}
                      />
                    )}
                  </m.div>
                </FramerMotion>
              </React.Fragment>
            ))}
          </div>
          <Pagination pagination={plp.pagination} />
          <DownloadAppBanner className="col-span-full" />
        </div>
      ) : (
        <NoResultsMessage />
      )}
    </div>
  );
};

function ProductCardInternal({
  product,
  allowNotifications,
  gridSize,
  index
}: {
  allowNotifications: boolean;
  gridSize: GridSize;
  index: number;
  product: ProductListItemInternal;
}) {
  function onProductCardClick(product: ProductListItem) {
    if (product.queryID) localStorage.setItem('queryID', product.queryID);
  }

  return (
    <ProductCard.Internal
      allowNotifications={allowNotifications}
      cardSize={gridSize}
      padImage={true}
      product={product}
      imageLoading={index < 10 ? 'eager' : 'lazy'}
      onClick={() => onProductCardClick(product)}
      pagePosition="PLP Main List"
    />
  );
}

function ProductCardExternal({
  product,
  allowNotifications,
  index
}: {
  allowNotifications: boolean;
  index: number;
  product: ProductListItemExternal;
}) {
  return (
    <ProductCard.External
      key={`product-${product.id}-${index}`}
      product={product}
      allowNotifications={allowNotifications}
      padImage
      className="h-auto min-w-40"
      imageLoading="eager"
      cardSize="small"
      pagePosition="PLP Quick Buy"
      overrideInteractionPagePosition={true}
    />
  );
}

const NoResultsMessage = () => {
  const { t } = useTranslation('plp');

  return (
    <h2 className="flex items-center justify-center py-6 text-center text-3xl font-bold">
      {t('noResults')}
    </h2>
  );
};

export default Products;
